/**
 * Client-side docs search (pure — no DOM). The index is built from the same
 * docs registry that drives the sidebar, resolved through the active locale's
 * translator, so search works in all six languages with zero extra payload:
 * page titles, section titles, meta descriptions, and h2/h3 headings.
 */
import type { DocSection } from './content/types';
import type { Translator } from '@/i18n/translator';

export interface DocSearchHeading {
  readonly id: string;
  readonly text: string;
}

export interface DocSearchEntry {
  readonly slug: string;
  readonly sectionTitle: string;
  readonly title: string;
  readonly description: string;
  readonly headings: readonly DocSearchHeading[];
  /** Definition-list terms on the page (e.g. glossary entries, config fields). */
  readonly terms?: readonly string[];
}

export interface DocSearchHit {
  readonly slug: string;
  readonly sectionTitle: string;
  readonly title: string;
  /** Anchor of the best-matching heading, when the match came from a heading. */
  readonly headingId?: string;
  readonly headingText?: string;
  readonly score: number;
}

/** Lowercase + strip combining marks so "wídget" finds "widget" in any locale. */
export function normalizeForSearch(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
}

/** Resolve the registry into a flat, locale-resolved search index. */
export function buildDocSearchIndex(
  sections: readonly DocSection[],
  t: Translator,
): DocSearchEntry[] {
  return sections.flatMap((section) =>
    section.pages.map((page) => ({
      slug: page.slug,
      sectionTitle: t(section.titleKey),
      title: t(page.titleKey),
      description: t(page.descriptionKey),
      headings: page.blocks
        .filter((b): b is Extract<typeof b, { kind: 'heading' }> => b.kind === 'heading')
        .map((h) => ({ id: h.id, text: t(h.textKey) })),
      terms: page.blocks
        .filter((b): b is Extract<typeof b, { kind: 'definitions' }> => b.kind === 'definitions')
        .flatMap((b) => b.items.map((item) => t(item.termKey))),
    })),
  );
}

const TITLE_PREFIX_SCORE = 100;
const TITLE_SCORE = 70;
const HEADING_SCORE = 55;
const SECTION_SCORE = 30;
const DESCRIPTION_SCORE = 25;

interface TermMatch {
  readonly score: number;
  readonly heading: DocSearchHeading | undefined;
}

function matchTerm(entry: DocSearchEntry, term: string): TermMatch | undefined {
  const title = normalizeForSearch(entry.title);
  if (title.startsWith(term)) return { score: TITLE_PREFIX_SCORE, heading: undefined };
  if (title.includes(term)) return { score: TITLE_SCORE, heading: undefined };
  const heading = entry.headings.find((h) => normalizeForSearch(h.text).includes(term));
  if (heading !== undefined) return { score: HEADING_SCORE, heading };
  if (normalizeForSearch(entry.sectionTitle).includes(term)) {
    return { score: SECTION_SCORE, heading: undefined };
  }
  if (normalizeForSearch(entry.description).includes(term)) {
    return { score: DESCRIPTION_SCORE, heading: undefined };
  }
  if ((entry.terms ?? []).some((term_) => normalizeForSearch(term_).includes(term))) {
    return { score: DESCRIPTION_SCORE, heading: undefined };
  }
  return undefined;
}

/**
 * Rank pages against a query. Every whitespace-separated term must match the
 * page somewhere (title, heading, section, or description — AND semantics);
 * scores sum across terms, title matches outrank heading matches, and a hit
 * carries its best-matching heading so the UI can deep-link to the anchor.
 */
export function searchDocs(
  index: readonly DocSearchEntry[],
  query: string,
  limit = 8,
): DocSearchHit[] {
  const terms = normalizeForSearch(query).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];

  const hits: DocSearchHit[] = [];
  for (const entry of index) {
    let score = 0;
    let heading: DocSearchHeading | undefined;
    let titleMatched = false;
    let matchedAll = true;
    for (const term of terms) {
      const match = matchTerm(entry, term);
      if (match === undefined) {
        matchedAll = false;
        break;
      }
      score += match.score;
      heading ??= match.heading;
      if (match.score >= TITLE_SCORE) titleMatched = true;
    }
    if (!matchedAll) continue;
    // Surface a heading anchor only when no term matched the title itself —
    // a title hit should land the reader at the top of the page.
    if (titleMatched) heading = undefined;
    hits.push({
      slug: entry.slug,
      sectionTitle: entry.sectionTitle,
      title: entry.title,
      ...(heading !== undefined ? { headingId: heading.id, headingText: heading.text } : {}),
      score,
    });
  }

  return hits.sort((a, b) => b.score - a.score).slice(0, limit);
}
